import { UserOut } from '@/services/noteGoApi/noteGoApiSchemas';

export interface GracePeriodStatus {
  isInGracePeriod: boolean;
  daysRemaining: number;
  hoursRemaining: number;
  minutesRemaining: number;
  expiresAt: Date | null;
}

/**
 * Determines if a user is in a grace period due to payment failure
 * Grace period occurs when subscription status is 'payment_failed' or 'past_due'
 * and there's still time remaining until current_period_end
 */
export function getGracePeriodStatus(userData: UserOut | null): GracePeriodStatus {
  if (!userData || !userData.subscription) {
    return {
      isInGracePeriod: false,
      daysRemaining: 0,
      hoursRemaining: 0,
      minutesRemaining: 0,
      expiresAt: null,
    };
  }

  const subscription = userData.subscription;

  // Check if subscription is in payment_failed or past_due status
  const isPaymentFailed = subscription.status === 'payment_failed' || subscription.status === 'past_due';

  if (!isPaymentFailed || !subscription.current_period_end) {
    return {
      isInGracePeriod: false,
      daysRemaining: 0,
      hoursRemaining: 0,
      minutesRemaining: 0,
      expiresAt: null,
    };
  }

  // Calculate time remaining from current_period_end
  const gracePeriodEndDate = new Date(subscription.current_period_end);
  const now = new Date();
  const millisecondsRemaining = gracePeriodEndDate.getTime() - now.getTime();

  // Only consider it an active grace period if there's time remaining
  // Return early if grace period has expired to avoid negative time calculations
  if (millisecondsRemaining <= 0) {
    return {
      isInGracePeriod: false,
      daysRemaining: 0,
      hoursRemaining: 0,
      minutesRemaining: 0,
      expiresAt: null,
    };
  }

  // Calculate days, hours, and minutes remaining (only when time is positive)
  const daysRemaining = Math.floor(millisecondsRemaining / (1000 * 60 * 60 * 24));
  const hoursRemaining = Math.floor((millisecondsRemaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutesRemaining = Math.floor((millisecondsRemaining % (1000 * 60 * 60)) / (1000 * 60));

  return {
    isInGracePeriod: true,
    daysRemaining,
    hoursRemaining,
    minutesRemaining,
    expiresAt: gracePeriodEndDate,
  };
}
